import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { groupsApi, type GroupCreateInput, type GroupUpdateInput } from '../api/groups';
import type { GroupKind } from '../api/types';
import { UndoContext } from './useUndoContext';

export const GROUPS_KEY = ['groups'] as const;

/** kind 지정 시 해당 섹션 그룹만 로드. */
export function useGroups(kind?: GroupKind) {
  return useQuery({
    queryKey: [...GROUPS_KEY, kind ?? 'all'],
    queryFn: () => groupsApi.list(kind).then((r) => r.items),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GroupCreateInput) => groupsApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: GroupUpdateInput }) =>
      groupsApi.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  // UndoContext — Provider 없으면 null. null이면 undo 미등록.
  const undoCtx = useContext(UndoContext);

  return useMutation({
    mutationFn: ({ id, cascade = true }: { id: number; cascade?: boolean; groupName?: string }) =>
      groupsApi.remove(id, cascade),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: GROUPS_KEY });
      // cascade 시 소속 항목도 무효 — 안전하게 모두 무효화
      qc.invalidateQueries({ queryKey: ['credentials'] });
      qc.invalidateQueries({ queryKey: ['notes'] });
      qc.invalidateQueries({ queryKey: ['items'] });
      // Undo 등록 — 그룹 복원 (cascade된 items도 restore API가 함께 복원)
      if (undoCtx) {
        const label = variables.groupName
          ? `"${variables.groupName}" 그룹 삭제됨`
          : '그룹 삭제됨';
        const { id } = variables;
        undoCtx.push({
          description: label,
          undo: async () => {
            await groupsApi.restore(id);
            qc.invalidateQueries({ queryKey: GROUPS_KEY });
            qc.invalidateQueries({ queryKey: ['items'] });
          },
        });
      }
    },
  });
}
